import type { Asset, AssetCategory } from '@/types/assets';
import type {
  ProductListItem,
  ProductResponse,
  ProductVariantResponse,
  DiscountCode,
  DiscountCodeListItem,
  DiscountUsageRecord,
  CatalogProductDetail,
  AdminCatalogProductsResponse,
  RenderJobResponse,
} from '@printtat/shared';
import { apiFetch, apiJson } from '@/lib/apiFetch';

export interface AdminUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isVerified: boolean;
  profilePicture: string | null;
  phoneNumber?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  orderCount: number;
}

export interface AdminUserDetail extends AdminUser {
  orders: AdminOrderListItem[];
}

export interface AdminOrderListItem {
  id: string;
  orderNumber: string;
  status: string;
  paymentMethod: string;
  paymentStatus: string;
  paymentMethodDetail: string | null;
  cardBrand: string | null;
  cardLastFour: string | null;
  itemCount: number;
  totalAmount: number;
  firstItemPreviewUrl: string | null;
  firstItemDesignName: string | null;
  customerName?: string;
  customerEmail?: string | null;
  createdAt: string;
}

export interface AdminOrdersResponse {
  orders: AdminOrderListItem[];
  total: number;
}

export interface AdminOrderDetail {
  id: string;
  orderNumber: string;
  status: string;
  paymentMethod: string;
  paymentStatus: string;
  shippingAddress: Record<string, unknown>;
  billingAddress: Record<string, unknown> | null;
  items: AdminOrderItem[];
  subtotal: number;
  discountAmount: number;
  discountDetails: Record<string, unknown> | null;
  shippingCost: number;
  totalAmount: number;
  notes: string | null;
  statusHistory: { status: string; timestamp: string; note?: string; changedBy?: string }[];
  customerName: string;
  customerEmail: string | null;
  customerId: string;
  paymentDetails: {
    transactionId: string | null;
    paymentMethod: string | null;
    cardBrand: string | null;
    cardLastFour: string | null;
    amountCents: number;
    currency: string;
    paidAt: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOrderItem {
  id: string;
  orderId: string;
  designName: string;
  productName: string;
  variantLabel: string;
  quantity: number;
  unitPrice: number;
  previewUrl: string | null;
  createdAt: string;
}

export interface AdminPaymentListItem {
  id: string;
  orderId: string;
  orderNumber: string | null;
  userId: string;
  customerName: string;
  customerEmail: string | null;
  paymobIntentionId: string;
  paymobTransactionId: string | null;
  amountCents: number;
  currency: string;
  status: string;
  paymentMethod: string | null;
  cardBrand: string | null;
  cardLastFour: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPaymentsResponse {
  payments: AdminPaymentListItem[];
  total: number;
}

export interface AdminPaymentEvent {
  id: string;
  type: string;
  severity: string | null;
  paymentId: string | null;
  paymobTransactionId: string | null;
  data: Record<string, unknown> | null;
  createdAt: string;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
}

export interface AdminProductListItem extends ProductListItem {
  activeVariantCount: number;
  totalVariantCount: number;
  totalStock: number;
  createdAt: string;
}

export interface AdminProductsResponse {
  products: AdminProductListItem[];
  total: number;
}

export interface AdminRenderQueueResponse {
  jobs: RenderJobResponse[];
  total: number;
}

export interface EmailUsageRow {
  category: string;
  sends: number;
}

export interface EmailDailyUsageRow {
  day: string;
  sends: number;
}

export interface EmailTopUserRow {
  user_id: string;
  email: string | null;
  sends: number;
}

export interface EmailUsageSummary {
  monthToDateTotal: number;
  monthToDateByCategory: EmailUsageRow[];
  daily: EmailDailyUsageRow[];
  topUsers: EmailTopUserRow[];
  tierCap: number;
  tierUsedPct: number;
}

export interface AwaitingReconciliationRow {
  orderId: string;
  orderNumber: string;
  totalAmount: string;
  ageMinutes: number;
  pendingPaymentCount: number;
  lastReconciliationAttempt: string | null;
  lastReconciliationOutcome: string | null;
  createdAt: string;
}

export interface ReconciliationStats {
  awaitingOver5Min: number;
  recoveredLast24h: number;
  attemptsLast24h: number;
  unreconciledFailedLast24h: number;
}

export interface ReconciliationEventRow {
  id: string;
  type: string;
  orderId: string | null;
  paymentId: string | null;
  paymobTransactionId: string | null;
  severity: string | null;
  data: Record<string, unknown> | null;
  createdAt: string;
}

export interface AdminRenderQueueStats {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  prioritized: number;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const STOREFRONT_URL = import.meta.env.VITE_STOREFRONT_URL || 'http://localhost:5177';

/** Resolve relative paths — /uploads go to backend, other static assets go to storefront */
export const resolveUrl = (url: string): string => {
  if (!url.startsWith('/')) return url;
  return url.startsWith('/uploads') ? `${API_URL}${url}` : `${STOREFRONT_URL}${url}`;
};

const normalizeAsset = (raw: Asset): Asset => ({
  ...raw,
  url: resolveUrl(raw.url),
});

interface AdminCategory extends AssetCategory {
  createdAt: string;
  updatedAt: string;
}

const buildQuery = (params: Record<string, unknown>): string => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
};

export const adminApi = {
  categories: {
    list(): Promise<AdminCategory[]> {
      return apiJson<AdminCategory[]>('/manage/asset-categories');
    },

    create(name: string): Promise<AdminCategory> {
      return apiJson<AdminCategory>('/manage/asset-categories', {
        method: 'POST',
        body: { name },
      });
    },

    update(id: string, data: { name?: string; sortOrder?: number }): Promise<AdminCategory> {
      return apiJson<AdminCategory>(`/manage/asset-categories/${id}`, {
        method: 'PATCH',
        body: data,
      });
    },

    async delete(id: string): Promise<void> {
      const res = await apiFetch(`/manage/asset-categories/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete category');
    },
  },

  assets: {
    async list(categoryId?: string): Promise<Asset[]> {
      const data = await apiJson<Asset[]>(`/manage/assets${categoryId ? `?category=${categoryId}` : ''}`);
      return data.map(normalizeAsset);
    },

    async upload(file: File, meta: { width: number; height: number }, categoryId?: string): Promise<Asset> {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('width', String(meta.width));
      fd.append('height', String(meta.height));
      if (categoryId) fd.append('categoryId', categoryId);
      return normalizeAsset(await apiJson<Asset>('/manage/assets/upload', { method: 'POST', body: fd }));
    },

    async uploadBulk(files: File[], metas: { width: number; height: number }[], categoryId?: string): Promise<Asset[]> {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      fd.append('metas', JSON.stringify(metas));
      if (categoryId) fd.append('categoryId', categoryId);
      const data = await apiJson<Asset[]>('/manage/assets/upload-bulk', { method: 'POST', body: fd });
      return data.map(normalizeAsset);
    },

    async update(id: string, data: { name?: string; categoryId?: string | null }): Promise<Asset> {
      return normalizeAsset(await apiJson<Asset>(`/manage/assets/${id}`, { method: 'PATCH', body: data }));
    },

    async delete(id: string): Promise<void> {
      const res = await apiFetch(`/manage/assets/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete asset');
    },

    deleteBulk(ids: string[]): Promise<{ deleted: number }> {
      return apiJson<{ deleted: number }>('/manage/assets/bulk-delete', { method: 'POST', body: { ids } });
    },
  },

  users: {
    list(query?: { search?: string; role?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<AdminUsersResponse> {
      return apiJson<AdminUsersResponse>(`/manage/users${buildQuery({ ...query })}`);
    },

    detail(id: string): Promise<AdminUserDetail> {
      return apiJson<AdminUserDetail>(`/manage/users/${id}`);
    },

    update(id: string, data: { firstName?: string; lastName?: string; role?: string; isVerified?: boolean }): Promise<AdminUser> {
      return apiJson<AdminUser>(`/manage/users/${id}`, { method: 'PATCH', body: data });
    },

    async delete(id: string): Promise<void> {
      const res = await apiFetch(`/manage/users/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error((e as { message?: string }).message || 'Failed to delete user');
      }
    },
  },

  orders: {
    list(query?: { status?: string; search?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<AdminOrdersResponse> {
      return apiJson<AdminOrdersResponse>(`/manage/orders${buildQuery({ ...query })}`);
    },

    detail(id: string): Promise<AdminOrderDetail> {
      return apiJson<AdminOrderDetail>(`/manage/orders/${id}`);
    },

    updateStatus(id: string, status: string, note?: string): Promise<unknown> {
      return apiJson<unknown>(`/manage/orders/${id}/status`, { method: 'PATCH', body: { status, note } });
    },

    async paymentEvents(id: string): Promise<AdminPaymentEvent[]> {
      const res = await apiFetch(`/manage/orders/${id}/payment-events`);
      if (!res.ok) return [];
      return res.json() as Promise<AdminPaymentEvent[]>;
    },
  },

  emailUsage: {
    summary(): Promise<EmailUsageSummary> {
      return apiJson<EmailUsageSummary>('/manage/email/usage');
    },
  },

  payments: {
    list(query?: { status?: string; search?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<AdminPaymentsResponse> {
      return apiJson<AdminPaymentsResponse>(`/manage/payments${buildQuery({ ...query })}`);
    },
  },

  products: {
    list(query?: { search?: string; category?: string; isActive?: boolean; page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<AdminProductsResponse> {
      return apiJson<AdminProductsResponse>(`/manage/products${buildQuery({ ...query })}`);
    },

    detail(id: string): Promise<ProductResponse> {
      return apiJson<ProductResponse>(`/manage/products/${id}`);
    },

    updateProduct(id: string, data: Record<string, unknown>): Promise<ProductResponse> {
      return apiJson<ProductResponse>(`/manage/products/${id}`, { method: 'PATCH', body: data });
    },

    updateVariant(productId: string, variantId: string, data: { isActive?: boolean; additionalPrice?: number; stockCount?: number }): Promise<ProductVariantResponse> {
      return apiJson<ProductVariantResponse>(`/manage/products/${productId}/variants/${variantId}`, { method: 'PATCH', body: data });
    },

    recalculatePrices(id: string): Promise<{ recalculated: number }> {
      return apiJson<{ recalculated: number }>(`/manage/products/${id}/recalculate-prices`, { method: 'POST' });
    },
  },

  discounts: {
    list(query?: { search?: string; type?: string; isActive?: boolean; page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<{ discounts: DiscountCodeListItem[]; total: number }> {
      return apiJson<{ discounts: DiscountCodeListItem[]; total: number }>(`/manage/discounts${buildQuery({ ...query })}`);
    },

    detail(id: string): Promise<DiscountCode> {
      return apiJson<DiscountCode>(`/manage/discounts/${id}`);
    },

    create(data: Record<string, unknown>): Promise<DiscountCode> {
      return apiJson<DiscountCode>('/manage/discounts', { method: 'POST', body: data });
    },

    update(id: string, data: Record<string, unknown>): Promise<DiscountCode> {
      return apiJson<DiscountCode>(`/manage/discounts/${id}`, { method: 'PATCH', body: data });
    },

    async remove(id: string): Promise<void> {
      const res = await apiFetch(`/manage/discounts/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete discount');
    },

    usages(id: string): Promise<{ usages: DiscountUsageRecord[] }> {
      return apiJson<{ usages: DiscountUsageRecord[] }>(`/manage/discounts/${id}/usages`);
    },
  },

  renderQueue: {
    list(query?: { status?: string; source?: string; search?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<AdminRenderQueueResponse> {
      return apiJson<AdminRenderQueueResponse>(`/manage/render-queue${buildQuery({ ...query })}`);
    },

    stats(): Promise<AdminRenderQueueStats> {
      return apiJson<AdminRenderQueueStats>('/manage/render-queue/stats');
    },

    submitRender(data: { garmentId: string; designId: string; productId?: string; surfaces: Array<{ surfaceKey: string; customDesignUrl: string }> }): Promise<{ jobId: string; status: string }> {
      return apiJson<{ jobId: string; status: string }>('/manage/render-queue/render', { method: 'POST', body: data });
    },
  },

  paymentReconciliation: {
    list(query?: { page?: number; limit?: number }): Promise<{ rows: AwaitingReconciliationRow[]; total: number }> {
      return apiJson<{ rows: AwaitingReconciliationRow[]; total: number }>(`/manage/payment-reconciliation${buildQuery({ ...query })}`);
    },

    stats(): Promise<ReconciliationStats> {
      return apiJson<ReconciliationStats>('/manage/payment-reconciliation/stats');
    },

    events(query?: { page?: number; limit?: number }): Promise<{ rows: ReconciliationEventRow[]; total: number }> {
      return apiJson<{ rows: ReconciliationEventRow[]; total: number }>(`/manage/payment-reconciliation/events${buildQuery({ ...query })}`);
    },

    reconcile(orderId: string): Promise<{ reconciled: boolean; paymentId?: string; transactionId?: string; reason?: string }> {
      return apiJson<{ reconciled: boolean; paymentId?: string; transactionId?: string; reason?: string }>(`/manage/payment-reconciliation/${orderId}/reconcile`, { method: 'POST' });
    },
  },

  catalog: {
    list(query?: { search?: string; category?: string; page?: number; limit?: number }): Promise<AdminCatalogProductsResponse> {
      return apiJson<AdminCatalogProductsResponse>(`/manage/catalog-products${buildQuery({ ...query })}`);
    },

    detail(id: string): Promise<CatalogProductDetail> {
      return apiJson<CatalogProductDetail>(`/manage/catalog-products/${id}`);
    },

    create(data: Record<string, unknown>): Promise<CatalogProductDetail> {
      return apiJson<CatalogProductDetail>('/manage/catalog-products', { method: 'POST', body: data });
    },

    update(id: string, data: Record<string, unknown>): Promise<CatalogProductDetail> {
      return apiJson<CatalogProductDetail>(`/manage/catalog-products/${id}`, { method: 'PATCH', body: data });
    },

    publish(id: string): Promise<CatalogProductDetail> {
      return apiJson<CatalogProductDetail>(`/manage/catalog-products/${id}/publish`, { method: 'PATCH' });
    },

    unpublish(id: string): Promise<CatalogProductDetail> {
      return apiJson<CatalogProductDetail>(`/manage/catalog-products/${id}/unpublish`, { method: 'PATCH' });
    },

    async remove(id: string): Promise<void> {
      const res = await apiFetch(`/manage/catalog-products/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete catalog product');
    },
  },
};
